import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import api from "../../api/axios";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";


const DEFAULT_AVATAR = "/assets/events/default_avatar.png";

const formatUnlockTime = (value, language) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language || undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "";
  }
};

const MissionStep = ({ number, children }) => (
  <div className="flex items-center gap-2.5 rounded-2xl bg-white/[0.08] px-3 py-2.5">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-300 text-[12px] font-black text-[#061A35]">
      {number}
    </span>
    <span className="text-[13px] font-medium leading-[16px] text-white/90">{children}</span>
  </div>
);

const BuddyAvatar = ({ member, onOpenVault, t }) => {
  const avatarSrc = resolveUserAvatarSrc(member, DEFAULT_AVATAR);
  const code = String(member?.code || "").trim();
  const canOpen = Boolean(code && typeof onOpenVault === "function");

  return (
    <button
      type="button"
      onClick={() => canOpen && onOpenVault(code)}
      disabled={!canOpen}
      className={`flex min-w-0 flex-1 flex-col items-center ${canOpen ? "" : "cursor-default"}`}
      aria-label={t("buddy_groups.open_profile", {
        name: code,
        defaultValue: `Open ${code}'s profile`,
      })}
    >
      <span className="relative">
        <img
          src={avatarSrc}
          alt=""
          className="h-14 w-14 rounded-full border-2 border-white/85 bg-[#0B234A] object-cover shadow-[0_8px_24px_rgba(4,12,32,0.35)]"
        />
        {member?.is_self ? (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-cyan-300 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.08em] text-[#061A35]">
            {t("buddy_groups.you", { defaultValue: "You" })}
          </span>
        ) : null}
      </span>
      {code ? (
        <span className="mt-2 max-w-[76px] truncate text-[12px] font-bold tracking-[0.08em] text-white">
          {code}
        </span>
      ) : null}
    </button>
  );
};

export default function EventBuddyPanel({
  eventId,
  buddyFeature,
  isParticipant,
  onOpenVault,
  className = "",
}) {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();
  const [payload, setPayload] = useState(buddyFeature || null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [releaseClock, setReleaseClock] = useState(() => Date.now());

  useEffect(() => {
    setPayload(buddyFeature || null);
  }, [buddyFeature, eventId]);

  const loadGroup = useCallback(async () => {
    if (!eventId || !isParticipant) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await api.get(`/events/${eventId}/buddy-group/`);
      setPayload(response?.data || null);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [eventId, isParticipant]);

  const releaseAtMs = useMemo(() => {
    const parsed = new Date(payload?.release_at || buddyFeature?.release_at || "").getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [buddyFeature?.release_at, payload?.release_at]);

  useEffect(() => {
    if (
      !isParticipant ||
      !buddyFeature?.enabled ||
      ["disabled", "participant_only", "payment_required", "confirmation_required", "expired"].includes(String(buddyFeature?.status || ""))
    ) {
      return undefined;
    }

    const now = releaseClock;
    if (buddyFeature?.status === "available" || (releaseAtMs && now >= releaseAtMs)) {
      loadGroup();
      return undefined;
    }

    if (!releaseAtMs) return undefined;
    const delay = Math.max(1000, Math.min(releaseAtMs - now, 60 * 1000));
    const timer = window.setTimeout(() => {
      if (Date.now() >= releaseAtMs) {
        loadGroup();
      } else {
        setReleaseClock(Date.now());
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [
    buddyFeature?.enabled,
    buddyFeature?.status,
    isParticipant,
    loadGroup,
    releaseClock,
    releaseAtMs,
  ]);

  if (
    !isParticipant ||
    !buddyFeature?.enabled ||
    ["disabled", "participant_only", "payment_required", "confirmation_required", "expired"].includes(String(payload?.status || ""))
  ) {
    return null;
  }

  const status = String(payload?.status || buddyFeature?.status || "");
  const unlockLabel = formatUnlockTime(
    payload?.release_at || buddyFeature?.release_at,
    i18n.resolvedLanguage || i18n.language
  );
  const members = Array.isArray(payload?.members) ? payload.members : [];

  return (
    <section
      className={`mb-4 overflow-hidden rounded-[28px] border border-cyan-200/25 bg-[linear-gradient(145deg,rgba(22,110,210,0.82),rgba(10,32,79,0.92))] p-4 text-left shadow-[0_18px_48px_rgba(3,12,35,0.3)] ${className}`}
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cyan-300 text-[22px] shadow-[0_8px_24px_rgba(74,222,255,0.24)]">
          👋
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-cyan-200">
            {t("buddy_groups.eyebrow", { defaultValue: "Your arrival crew" })}
          </p>
          <h2 className="mt-1 text-[20px] font-bold leading-[23px] text-white">
            {status === "ready"
              ? t("buddy_groups.ready_title", { defaultValue: "Meet your event crew" })
              : status === "forming"
                ? t("buddy_groups.forming_title", { defaultValue: "We're assembling your crew" })
                : t("buddy_groups.scheduled_title", {
                    date: unlockLabel,
                    defaultValue: `Your crew unlocks ${unlockLabel}`,
                  })}
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-3">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-14 w-14 animate-pulse rounded-full bg-white/15"
            />
          ))}
        </div>
      ) : status === "ready" ? (
        <>
          <p className="mt-3 text-[14px] leading-[18px] text-white/80">
            {t("buddy_groups.ready_description", {
              defaultValue:
                "Say hello before the event. When you arrive, share where you are and find one another.",
            })}
          </p>

          <div className="mt-4 flex items-start justify-between gap-1">
            {members.map((member) => (
              <BuddyAvatar
                key={member.user_id}
                member={member}
                onOpenVault={onOpenVault}
                t={t}
              />
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <MissionStep number="1">
              {t("buddy_groups.steps.say_hello", { defaultValue: "Say hello in your crew chat" })}
            </MissionStep>
            <MissionStep number="2">
              {t("buddy_groups.steps.departure", { defaultValue: "Tell the crew when you leave" })}
            </MissionStep>
            <MissionStep number="3">
              {t("buddy_groups.steps.arrival_photo", { defaultValue: "Send a photo when you arrive" })}
            </MissionStep>
            <MissionStep number="4">
              {t("buddy_groups.steps.meetup", { defaultValue: "Find a crew member and scan the QR" })}
            </MissionStep>
          </div>

          <button
            type="button"
            onClick={() => navigate(payload?.chat_path || `/menu/chats/group/${payload?.chat_id}`)}
            className="mt-4 flex h-[52px] w-full items-center justify-center gap-2 rounded-full bg-white px-5 text-[16px] font-bold text-blue-700 shadow-[0_10px_28px_rgba(4,13,39,0.22)]"
          >
            <span aria-hidden="true">💬</span>
            {t("buddy_groups.open_chat", { defaultValue: "Open crew chat" })}
          </button>
        </>
      ) : (
        <>
          <p className="mt-3 text-[14px] leading-[18px] text-white/80">
            {status === "forming"
              ? t("buddy_groups.forming_description", {
                  defaultValue:
                    "As soon as at least three participants are ready, we'll introduce you and open a private chat.",
                })
              : t("buddy_groups.scheduled_description", {
                  defaultValue:
                    "A day before the event, we'll introduce you to 2–3 people so you already have company when you arrive.",
                })}
          </p>
          {loadFailed ? (
            <button
              type="button"
              onClick={loadGroup}
              className="mt-3 text-[13px] font-semibold text-cyan-100 underline underline-offset-4"
            >
              {t("buddy_groups.retry", { defaultValue: "Try again" })}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
